import { useMutation } from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addActivity, UserActivity } from '~/schemas/userActivity';
import { CREATE_COMMENT, UPDATE_COMMENT } from '~/services/comment';

interface ActivityDialogProps {
  open: boolean;
  onClose: () => void;
  activity: UserActivity | addActivity;
  type: string;
  onCommentUpdate: (comment: any) => void; // New prop
}

const ActivityDialog: React.FC<ActivityDialogProps> = ({
  open,
  onClose,
  activity,
  type,
  onCommentUpdate,
}) => {
  const [t] = useTranslation('activity');
  const [updateComment] = useMutation(UPDATE_COMMENT);
  const [createComment] = useMutation(CREATE_COMMENT);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [comment, setComment] = useState(activity.comment || '');
  const [typeOfComment, setTypeOfComment] = useState('question'); // New state

  useEffect(() => {
    if (open) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (isModalOpen) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isModalOpen]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    onClose();
  };

  const handleEditObservation = async () => {
    const commentData = await updateComment({
      variables: {
        data: {
          comment_id: activity._id,
          comment,
          typeOfComment: typeOfComment,
        },
      },
    });
    onCommentUpdate(commentData);
    setIsModalOpen(false);
  };

  const handleAddObservation = async () => {
    const newCommnet = await createComment({
      variables: {
        data: {
          person_id: activity.person_id,
          agent_id: activity.agent_id,
          comment: comment,
          typeOfComment: typeOfComment, // Include typeOfComment
        },
      },
    });
    newCommnet.data.create_comment.data.activity_type = 'COMMENTS';
    onCommentUpdate(newCommnet);
    setIsModalOpen(false);
  };

  return (
    <div>
      <dialog
        ref={dialogRef}
        onClose={handleCloseModal}
        className="modal"
        onClick={handleCloseModal}
      >
        <div
          className="modal-box flex flex-col gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-bold">
            {type === 'add' ? t('add_observation') : t('edit_observation')}
          </h3>
          <p>{t('select_type')}</p>

          <select
            className="select select-bordered mt-0 w-full"
            value={typeOfComment} // Bind value to state
            onChange={(e) => setTypeOfComment(e.target.value)} // Update state on change
          >
            <option value="question">{t('question')}</option>
            <option value="feedback">{t('feedback')}</option>
            <option value="issue">{t('issue')}</option>
            <option value="other">{t('other')}</option>
          </select>

          <p>{t('enter_observation')}</p>
          <textarea
            className="textarea textarea-bordered w-full"
            placeholder={t('enter_observation')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          ></textarea>
          <div className="modal-action">
            <button className="btn" onClick={handleCloseModal}>
              {t('cancel')}
            </button>

            <button
              className="btn btn-primary rounded-full"
              onClick={
                type === 'add' ? handleAddObservation : handleEditObservation
              }
            >
              {type === 'add' ? t('add_observation') : t('edit_observation')}
            
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default ActivityDialog;
